import { convertToLocalDateTime } from "components/helpers";
import { Check, X } from "lucide-react";

import { Spinner } from "@/components/ui/spinner";
import { selectColumn } from "components/BaseTableV3/selectColumn";
import TagList from "components/TagList";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import CallModal from "components/modals/CallModal";
import DepartmentService from "services/Department";
import EntityService from "services/Entity";
import CampaignService from "services/Campaign";
import { ContactTableActions } from "./ContactTableActions";
import { getUsersForFiltersQuery } from "helpers/user";
import { NULLABLE_OPERATORS } from "components/NewAdvancedSearch/filters/selectOperators";

const BooleanFlagCell = ({ value }) =>
  value ? (
    <Check className="tw:size-4 tw:text-success" aria-label="Yes" />
  ) : (
    <X className="tw:size-4 tw:text-destructive" aria-label="No" />
  );

export const getContactsTableColumns = ({
  handleDeleteContact,
  invalidateData,
  enableCalling,
  isVoicemailsFetched,
  voicemails,
}) => {
  return [
    selectColumn,
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.getValue("ID")}`}>
          {row.getValue("ID")}
        </TableLink>
      ),
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.getValue("Tags")}
          modelType="Contact"
          modelId={row.getValue("ID")}
          onUpdate={invalidateData}
          tableCell
        />
      ),
    },
    {
      header: "Name",
      id: "Name",
      accessorKey: "full_name",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.getValue("ID")}`}>
          {row.getValue("Name")}
        </TableLink>
      ),
    },
    {
      header: "Alternate Names",
      id: "Alternate Names",
      accessorKey: "alternate_names",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("Alternate Names").map(({ id, name }) => (
            <li key={id}>{name}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Email",
      id: "Email",
      accessorKey: "email",
    },
    {
      header: "Emailable",
      id: "Emailable",
      accessorKey: "emailable",
      enableSorting: false,
      cell: ({ row }) => <BooleanFlagCell value={row.getValue("Emailable")} />,
    },
    {
      header: "Callable",
      id: "Callable",
      accessorKey: "callable",
      enableSorting: false,
      cell: ({ row }) => <BooleanFlagCell value={row.getValue("Callable")} />,
    },
    {
      header: "Department",
      id: "Department",
      accessorKey: "department_name",
    },
    {
      header: "Alternate Emails",
      id: "Alternate Emails",
      accessorKey: "email_addresses",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row
            .getValue("Alternate Emails")
            .filter(email => !email.is_primary)
            .map(({ id, email }) => (
              <li key={id}>{email}</li>
            ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Phone Numbers",
      id: "Phone Numbers",
      accessorKey: "phone_numbers",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("Phone Numbers").map(phone => (
            <li key={phone.id}>
              {phone.ext ? `${phone.number}x${phone.ext}` : phone.number}
            </li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Timezone",
      id: "Timezone",
      accessorKey: "timezone",
    },
    {
      header: "Entity",
      id: "Entity",
      accessorKey: "entity_name",
      cell: ({ row }) => (
        <TableLink href={`/entities/${row.original.entity_id}`}>
          {row.getValue("Entity")}
        </TableLink>
      ),
    },
    {
      header: "Title",
      id: "Title",
      accessorKey: "title",
    },
    {
      header: "Campaigns",
      id: "Campaigns",
      accessorKey: "campaign_targets",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("Campaigns").map(({ campaign_id, campaign_name }) => (
            <li key={`${row.original.id}-${campaign_id}`}>
              <TableLink href={`/campaigns/${campaign_id}`}>
                {campaign_name}
              </TableLink>
            </li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Account Managers",
      id: "Account Managers",
      accessorKey: "account_managers",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row
            .getValue("Account Managers")
            .map(({ user_id, user_full_name }) => (
              <li key={`${row.original.id}-${user_id}`}>{user_full_name}</li>
            ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Note Count",
      id: "Note Count",
      accessorKey: "notes_count",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.getValue("ID")}/notes`}>
          {row.getValue("Note Count") ? row.getValue("Note Count") : 0}
        </TableLink>
      ),
    },
    {
      header: "Last Activity Date",
      id: "Last Activity Date",
      accessorKey: "last_activity_date",
      cell: ({ row }) => (
        <div>
          {row.getValue("Last Activity Date")
            ? convertToLocalDateTime(row.getValue("Last Activity Date")).format(
                "YYYY-MM-DD"
              )
            : null}
        </div>
      ),
    },
    ...(enableCalling
      ? [
          {
            header: "Call",
            id: "Call",
            enableSorting: false,
            cell: ({ row }) => {
              const contact = row.original;
              return isVoicemailsFetched ? (
                <CallModal
                  contactId={contact.id}
                  disabled={
                    !contact.phone_numbers.length ||
                    !contact.phone_numbers.some(
                      ({ is_callable }) => is_callable
                    ) ||
                    !contact.phone_numbers.some(({ enabled }) => enabled)
                  }
                  voicemails={voicemails}
                />
              ) : (
                <Spinner />
              );
            },
          },
        ]
      : []),
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      size: 60,
      enableSorting: false,
      enableResizing: false,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <ContactTableActions
          contact={row.original}
          handleDeleteContact={handleDeleteContact}
        />
      ),
    },
  ];
};

export const getContactsTableFilters = ({ tags, entityId }) => {
  return [
    {
      key: "last_activity_date",
      title: "Last Activity",
      type: "dateRange",
    },
    {
      key: "search:contact_name",
      title: "Contact Name",
      type: "input",
    },
    {
      key: "search:contact_email",
      title: "Contact Email",
      type: "input",
    },
    {
      key: "search:contact_phone",
      title: "Contact Phone",
      type: "input",
    },
    {
      key: "department_id",
      title: "Department Name",
      type: "ruvixxSelect",
      queryFn: DepartmentService.getForFilters,
    },
    {
      key: "account_manager_id",
      title: "Account Manager",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: getUsersForFiltersQuery,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "assigned_user_id",
      title: "Assignee",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: getUsersForFiltersQuery,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    ...(!entityId
      ? [
          {
            key: "entity_id",
            title: "Entity",
            type: "ruvixxSelect",
            queryFn: EntityService.getEntitiesForFilters,
          },
        ]
      : []),
    {
      key: "campaign_id",
      title: "Campaign",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: CampaignService.getCampaignsForFilters,
      props: {
        includeNoneOption: true,
      },
    },
    {
      title: "Outreach Permissions",
      key: "grid_1",
      type: "grid",
      components: [
        { key: "is_callable", title: "Callable" },
        { key: "not_callable", title: "Not Callable" },
        { key: "is_emailable", title: "Emailable" },
        { key: "not_emailable", title: "Not Emailable" },
      ],
    },
  ];
};
